import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
    styled,
    Box,
    Paper,
    List,
    ListItem as ListItemUnstyled,
    ListItemAvatar,
    ListItemText,
    Divider,
    Typography,
    // wrap
} from '@mui/material';
import { MarkerClusterer, OverlayView } from '@react-google-maps/api';
import { getMarkerMap } from '../utils';
import { toAmountString } from '../../../schedule2/utils';
import clusterIcon from '../../symbols/processor-cluster.svg?url';

const clusterStyles = [
    {
        textColor: 'white',
        url: clusterIcon,
        width: 32,
        height: 32,
        textSize: 14,
    },
];

const OverlayWrap = styled(Box)(() => ({
    position: 'relative',
    display: 'inline-block',
    zIndex: 5000,
}));

const OverlayPaper = styled(Paper)(() => ({
    minWidth: 200,
    zIndex: 500,
    maxHeight: 400,
    overflowY: 'auto',
}));

const OverlayArrow = styled(Box)(() => ({
    position: 'absolute',
    top: '50%',
    left: '-9px',
    width: 0,
    height: 0,
    borderTop: '9px solid transparent',
    borderBottom: '9px solid transparent',
    borderRight: '9px solid white',
    transform: 'translateY(-50%)',
    zIndex: 1000,
}));

const ListItem = styled(ListItemUnstyled)(() => ({
    padding: 0,
    paddingLeft: 10,
    paddingRight: 20,
}));

const ClusteredItemList = ({ selectedCluster, setHoveringOverlay }) => {
    const markerMap = getMarkerMap();
    const { position, items } = selectedCluster;

    const dateSortedItems = items.sort((a, b) => {
        const dateA = new Date(a.date || a.created_at);
        const dateB = new Date(b.date || b.created_at);
        return dateA - dateB;
    });

    return (
        <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(_, height) => ({
                x: 35,
                y: -height / 2,
            })}
            // wrap
        >
            <OverlayWrap
                onMouseEnter={() => setHoveringOverlay(true)}
                onMouseLeave={() => setHoveringOverlay(false)}
                // wrap
            >
                <OverlayPaper>
                    <OverlayArrow />
                    <List dense>
                        {dateSortedItems.map((item, idx) => {
                            const { _id, type, date, name, volume, user_id, created_at, created_by } = item;
                            if (!type || !(type in markerMap)) {
                                return null;
                            }
                            const { label, marker } = markerMap[type];
                            const timeStamp = date || created_at;
                            const nameField = name || user_id?.name || created_by?.name;

                            return (
                                <>
                                    <ListItem key={_id}>
                                        <ListItemAvatar>{marker}</ListItemAvatar>
                                        <ListItemText
                                            primary={label}
                                            secondary={
                                                <Box display="flex" flexDirection="column">
                                                    {nameField && (
                                                        <Typography variant="caption" fontWeight="bold">
                                                            {nameField}
                                                        </Typography>
                                                    )}
                                                    {timeStamp && (
                                                        <Typography
                                                            variant="caption"
                                                            // wrap
                                                        >
                                                            {moment(timeStamp).format('DD/MM/YY - hh:mm A')}
                                                        </Typography>
                                                    )}
                                                    {volume && (
                                                        <Typography
                                                            variant="caption"
                                                            // wrap
                                                        >{`${toAmountString(volume)} lbs`}</Typography>
                                                    )}
                                                </Box>
                                            }
                                            // wrap
                                        />
                                    </ListItem>
                                    {idx !== items.length - 1 && (
                                        <Box>
                                            <Divider variant="middle" component="li" />
                                        </Box>
                                    )}
                                </>
                            );
                        })}
                    </List>
                </OverlayPaper>
            </OverlayWrap>
        </OverlayView>
    );
};

const ClusteredColourDotOverlay = ({ clusters }) => {
    const dotSize = 12;
    const gapSize = 4;

    return (
        <>
            {clusters
                .filter((cluster) => cluster.colours.length > 1)
                .map((cluster, idx) => {
                    const totalDots = cluster.colours.length;
                    const totalWidth = totalDots * (dotSize + gapSize) - gapSize;
                    const offset = totalWidth / 2;

                    return (
                        <OverlayView
                            key={`${cluster.position.lat}`}
                            position={cluster.position}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            getPixelPositionOffset={() => ({
                                x: 0,
                                y: 25,
                            })}
                        >
                            <Box
                                display="flex"
                                gap={0.5}
                                sx={{
                                    marginLeft: `-${offset}px`,
                                }}
                            >
                                {cluster.colours.map((colour, i) => (
                                    <Box
                                        key={colour}
                                        sx={{
                                            width: dotSize,
                                            height: dotSize,
                                            borderRadius: '50%',
                                            backgroundColor: colour,
                                            border: '1px solid white',
                                        }}
                                    />
                                ))}
                            </Box>
                        </OverlayView>
                    );
                })}
        </>
    );
};

const PlaybackClusterer = ({ renderFn }) => {
    const [hoveringCluster, setHoveringCluster] = useState(false);
    const [hoveringOverlay, setHoveringOverlay] = useState(false);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [clusterColourDots, setClusterColourDots] = useState([]);

    const handleClusterHover = (cluster) => {
        const lat = cluster.center.lat();
        const lng = cluster.center.lng();
        const markers = cluster.getMarkers();
        const clusteredItemData = markers.map((marker) => marker.__data).filter(Boolean);

        setSelectedCluster({
            position: { lat, lng },
            items: clusteredItemData,
        });
    };

    const handleClustersChanged = (clusters) => {
        const markerMap = getMarkerMap();
        const newClusterColorMap = clusters.map((cluster) => {
            const markers = cluster.getMarkers();
            const position = cluster.getCenter?.() || cluster.center;
            const uniqueColors = Array.from(
                new Set(
                    markers
                        .map((marker) => marker.__data?.type)
                        .map((type) => markerMap[type]?.colour)
                        .filter(Boolean)
                )
            );
            return {
                position: {
                    lat: position.lat(),
                    lng: position.lng(),
                },
                colours: uniqueColors,
            };
        });

        setClusterColourDots(newClusterColorMap);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!hoveringCluster && !hoveringOverlay) {
                setSelectedCluster(null);
            }
        }, 100);

        return () => clearTimeout(timeout);
    }, [hoveringCluster, hoveringOverlay]);

    return (
        <>
            <MarkerClusterer
                gridSize={20}
                minimumClusterSize={2}
                averageCenter
                options={{ styles: clusterStyles }}
                onClusteringEnd={(clusterer) => {
                    const clusters = clusterer.getClusters();
                    handleClustersChanged(clusters);
                }}
                onMouseOver={(cluster) => {
                    setHoveringCluster(true);
                    handleClusterHover(cluster);
                }}
                onMouseOut={() => setHoveringCluster(false)}
                onClick={() => setSelectedCluster(null)}
                // wrap
            >
                {(clusterer) => (
                    <>
                        {renderFn(clusterer)}
                        <ClusteredColourDotOverlay clusters={clusterColourDots} />
                    </>
                )}
            </MarkerClusterer>
            {selectedCluster && (
                <ClusteredItemList
                    selectedCluster={selectedCluster}
                    setHoveringOverlay={setHoveringOverlay}
                    // wrap
                />
            )}
        </>
    );
};

export default PlaybackClusterer;
